import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminSectionBaseComponent } from 'src/app/shared/admin-section-base';
import { ConfirmDeleteDialogComponent } from '../shared/confirm-delete-dialog/confirm-delete-dialog.component';
import { AdminApiService, PrismaAlert } from 'src/app/shared/services/admin-api.service';

@Component({
  standalone: false,
  selector: 'app-manage-alerts',
  templateUrl: './manage-alerts.component.html',
  styleUrls: ['./manage-alerts.component.scss'],
})
export class ManageAlertsComponent extends AdminSectionBaseComponent implements OnInit {
  private readonly prismaSuggestionTimeoutMs = 120000;
  prismaAlerts: PrismaAlert[] = [];
  alertsSearchTerm = '';
  alertsAssessmentFilter: 'all' | 'malicious' | 'suspicious' | 'benign' | 'none' = 'all';
  readonly alertAssessmentOptions: Array<{ value: 'all' | 'malicious' | 'suspicious' | 'benign' | 'none'; label: string }> = [
    { value: 'all', label: 'All assessments' },
    { value: 'malicious', label: 'Malicious' },
    { value: 'suspicious', label: 'Suspicious' },
    { value: 'benign', label: 'Benign' },
    { value: 'none', label: 'Not assessed' },
  ];
  alertsAgeFilter: 'all' | '1d' | '7d' | '30d' | '90d' | '180d' | '365d' = 'all';
  readonly alertAgeOptions: Array<{ value: 'all' | '1d' | '7d' | '30d' | '90d' | '180d' | '365d'; label: string; maxAgeDays: number | null }> = [
    { value: 'all', label: 'Default range (server default)', maxAgeDays: null },
    { value: '1d', label: 'Last 24 hours', maxAgeDays: 1 },
    { value: '7d', label: 'Last week', maxAgeDays: 7 },
    { value: '30d', label: 'Last month', maxAgeDays: 30 },
    { value: '90d', label: 'Last 3 months', maxAgeDays: 90 },
    { value: '180d', label: 'Last 6 months', maxAgeDays: 180 },
    { value: '365d', label: 'Last year', maxAgeDays: 365 },
  ];
  selectedAlert: PrismaAlert | null = null;
  suspendAlertConfirm: PrismaAlert | null = null;
  readonly defaultAlertSuggestion = 'No automated suggestion available yet. Review the alert details and suspend the user if the activity appears malicious.';

  private alertsView: { src: PrismaAlert[] | null; age: string; assess: string; q: string; ver: number; value: PrismaAlert[] } = { src: null, age: '', assess: '', q: '', ver: -1, value: [] };
  private alertsVersion = 0;
  private lastAlertsAgeFilter: 'all' | '1d' | '7d' | '30d' | '90d' | '180d' | '365d' = this.alertsAgeFilter;

  constructor(
    private adminApiService: AdminApiService,
    private dialog: MatDialog,
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef,
  ) {
    super(snackBar, cdr);
    this.initPagination('alerts', 10);
  }

  ngOnInit(): void {
    this.refreshPrismaAlerts();
  }

  refreshPrismaAlerts(): void {
    const maxAgeDays = this.getSelectedAlertMaxAgeDays();
    const lookbackDays = maxAgeDays ?? undefined;
    this.trackApiCall(this.adminApiService.listPrismaAlerts(this.token, lookbackDays)).subscribe({
      next: (alerts) => { this.prismaAlerts = alerts; },
      error: (error) => this.handleError(error),
    });
  }

  getDisplayedAlerts(): PrismaAlert[] {
    if (
      this.alertsView.src === this.prismaAlerts &&
      this.alertsView.age === this.alertsAgeFilter &&
      this.alertsView.assess === this.alertsAssessmentFilter &&
      this.alertsView.q === this.alertsSearchTerm &&
      this.alertsView.ver === this.alertsVersion
    ) {
      return this.alertsView.value;
    }

    let alerts = this.prismaAlerts;

    const maxAgeDays = this.getSelectedAlertMaxAgeDays();
    if (maxAgeDays !== null) {
      const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;
      alerts = alerts.filter((a) => {
        if (!a.startedAt) { return false; }
        const started = new Date(a.startedAt).getTime();
        return !isNaN(started) && started >= cutoff;
      });
    }

    if (this.alertsAssessmentFilter !== 'all') {
      alerts = alerts.filter((a) => {
        const assessment = this.getAlertAssessment(a);
        return this.alertsAssessmentFilter === 'none'
          ? assessment === ''
          : assessment === this.alertsAssessmentFilter;
      });
    }

    const q = this.alertsSearchTerm.trim().toLowerCase();
    if (q) {
      alerts = alerts.filter((a) =>
        (a.name || '').toLowerCase().includes(q) ||
        (a.resource || '').toLowerCase().includes(q) ||
        (a.userEmail || '').toLowerCase().includes(q) ||
        this.getAlertAssessment(a).includes(q)
      );
    }

    this.alertsView = {
      src: this.prismaAlerts,
      age: this.alertsAgeFilter,
      assess: this.alertsAssessmentFilter,
      q: this.alertsSearchTerm,
      ver: this.alertsVersion,
      value: alerts,
    };
    return alerts;
  }

  // Resets the alerts table to the first page whenever a filter changes so the
  // user isn't stranded on an out-of-range page.
  onAlertsFilterChange(): void {
    this.resetPage('alerts');

    if (this.alertsAgeFilter !== this.lastAlertsAgeFilter) {
      this.lastAlertsAgeFilter = this.alertsAgeFilter;
      this.refreshPrismaAlerts();
    }
  }

  private getSelectedAlertMaxAgeDays(): number | null {
    const option = this.alertAgeOptions.find((o) => o.value === this.alertsAgeFilter);
    return option ? option.maxAgeDays : null;
  }

  getAlertAge(startedAt: string | null): string {
    if (!startedAt) { return 'Unknown'; }
    const started = new Date(startedAt).getTime();
    if (isNaN(started)) { return 'Unknown'; }
    const diffMs = Math.max(0, Date.now() - started);
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days >= 365) { const years = Math.floor(days / 365); return `${years}y ago`; }
    if (days >= 30) { const months = Math.floor(days / 30); return `${months}mo ago`; }
    if (days >= 7) { const weeks = Math.floor(days / 7); return `${weeks}w ago`; }
    if (days >= 1) { return `${days}d ago`; }
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours >= 1) { return `${hours}h ago`; }
    const mins = Math.floor(diffMs / (1000 * 60));
    return `${mins}m ago`;
  }

  getAlertSuggestion(alert: PrismaAlert): string {
    return alert.suggestion || this.defaultAlertSuggestion;
  }

  hasAlertSuggestion(alert: PrismaAlert): boolean {
    return !!alert.suggestion;
  }

  // Derives the model's severity assessment (benign | suspicious | malicious)
  // from the cached suggestion. The suggestion string is formatted as
  // "[<assessment> / <confidence> confidence] ...", so we parse the leading tag.
  getAlertAssessment(alert: PrismaAlert): string {
    if (!alert.suggestion) { return ''; }
    const match = alert.suggestion.match(/^\[\s*([a-zA-Z]+)\s*\//);
    return match ? match[1].toLowerCase() : '';
  }

  getAlertAssessmentClass(alert: PrismaAlert): string {
    const assessment = this.getAlertAssessment(alert);
    return assessment ? `alert-assessment-${assessment}` : 'alert-assessment-none';
  }

  getAlertAssessmentLabel(alert: PrismaAlert): string {
    const assessment = this.getAlertAssessment(alert);
    return assessment ? assessment.toUpperCase() : 'NOT ASSESSED';
  }

  alertSuggestionActionKey(id: string): string { return `alert-suggestion:${id}`; }

  regenerateAlertSuggestion(alert: PrismaAlert): void {
    const actionKey = this.alertSuggestionActionKey(alert.id);
    const force = this.hasAlertSuggestion(alert);
    this.trackActionCall(actionKey, this.adminApiService.generatePrismaAlertSuggestion(this.token, alert, force), this.prismaSuggestionTimeoutMs).subscribe({
      next: (result) => {
        const suggestion = result?.suggestion || '';
        if (suggestion) {
          alert.suggestion = suggestion;
          const match = this.prismaAlerts.find((a) => a.id === alert.id);
          if (match) { match.suggestion = suggestion; }
          this.alertsVersion += 1;
          this.showSuccess('Suggestion updated.');
        } else {
          this.showError('No suggestion was returned. Check the LLM configuration.');
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  getAlertLabelEntries(alert: PrismaAlert | null): Array<{ key: string; value: string }> {
    if (!alert || !alert.labels) { return []; }
    return Object.keys(alert.labels).map((key) => ({ key, value: alert.labels[key] }));
  }

  openAlertInfo(alert: PrismaAlert): void { this.selectedAlert = alert; }
  closeAlertInfo(): void { this.selectedAlert = null; }

  confirmSuspendFromAlert(alert: PrismaAlert): void { this.suspendAlertConfirm = alert; }
  cancelSuspendFromAlert(): void { this.suspendAlertConfirm = null; }

  suspendUserFromAlert(): void {
    if (!this.suspendAlertConfirm) { return; }
    const alert = this.suspendAlertConfirm;
    this.suspendAlertConfirm = null;
    if (!alert.userEmail) {
      this.showError('No user is associated with this alert.');
      return;
    }
    const actionKey = this.alertSuspendActionKey(alert.id);
    this.trackActionCall(actionKey, this.adminApiService.userMaintenance(this.token, alert.userEmail, 'Suspend')).subscribe({
      next: () => {
        this.markAlertSuspended(alert);
        this.showSuccess(`Suspended ${alert.userEmail}.`);
      },
      error: (error) => this.handleError(error),
    });
  }

  // Records the suspension against the alert so it becomes eligible to dismiss,
  // and reflects it locally without waiting for a refresh.
  private markAlertSuspended(alert: PrismaAlert): void {
    this.adminApiService.markPrismaAlertSuspended(this.token, alert.id).subscribe({
      next: () => {
        this.setAlertFlag(alert.id, { userSuspended: true });
      },
      error: (error) => {
        this.showError('Failed to persist the alert suspension marker. Please refresh and/or close the alert before dismissing.');
        console.error('Failed to persist alert suspension marker:', error);
      },
    });
  }

  // Whether an alert may be dismissed: only after it has been closed or its user
  // has been suspended.
  canDismissAlert(alert: PrismaAlert): boolean {
    return !!alert.closed || !!alert.userSuspended;
  }

  closeAlert(alert: PrismaAlert): void {
    const actionKey = this.alertCloseActionKey(alert.id);
    this.trackActionCall(actionKey, this.adminApiService.closePrismaAlert(this.token, alert.id)).subscribe({
      next: () => {
        this.setAlertFlag(alert.id, { closed: true, closedAt: new Date().toISOString() });
        this.showSuccess('Alert closed.');
      },
      error: (error) => this.handleError(error),
    });
  }

  // Applies flags to the alert in the list and the open modal so the UI updates
  // immediately.
  private setAlertFlag(id: string, flags: Partial<PrismaAlert>): void {
    const match = this.prismaAlerts.find((a) => a.id === id);
    if (match) { Object.assign(match, flags); }
    if (this.selectedAlert && this.selectedAlert.id === id) { Object.assign(this.selectedAlert, flags); }
    this.alertsVersion += 1;
  }

  deleteAlert(alert: PrismaAlert): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Dismiss Alert',
        message: 'Are you sure you want to permanently dismiss this alert?',
        itemName: `Alert ${alert.id}`
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.removeAlert(alert, this.alertDeleteActionKey(alert.id), 'Alert dismissed.');
      }
    });
  }

  private removeAlert(alert: PrismaAlert, actionKey: string, successMessage?: string): void {
    this.trackActionCall(actionKey, this.adminApiService.deletePrismaAlert(this.token, alert.id)).subscribe({
      next: () => {
        this.prismaAlerts = this.prismaAlerts.filter((a) => a.id !== alert.id);
        if (this.selectedAlert && this.selectedAlert.id === alert.id) { this.selectedAlert = null; }
        if (successMessage) { this.showSuccess(successMessage); }
      },
      error: (error) => this.handleError(error),
    });
  }

  isAlertBusy(alert: PrismaAlert): boolean {
    return this.isActionBusy(this.alertSuggestionActionKey(alert.id))
      || this.isActionBusy(this.alertSuspendActionKey(alert.id))
      || this.isActionBusy(this.alertCloseActionKey(alert.id))
      || this.isActionBusy(this.alertDeleteActionKey(alert.id));
  }

  alertSuspendActionKey(id: string): string { return `alert-suspend:${id}`; }
  alertCloseActionKey(id: string): string { return `alert-close:${id}`; }
  alertDeleteActionKey(id: string): string { return `alert-delete:${id}`; }

  trackByAlertId(_index: number, alert: PrismaAlert): string {
    return alert.id;
  }
}
